import { runtimeBus, type DangerLevel } from '../app/runtimeBus';
import { characterAssetPath } from '../game/assets/manifest';
import { getCard, type CardId } from '../game/content/cards';
import { CHARACTERS, getCharacter } from '../game/content/characters';
import { OBSERVER_STAGE } from '../game/content/progression';
import { DEFAULT_GAME_MODE, GAME_MODES, getGameMode, phaseName, presentationPhase } from '../game/content/modes';
import { CARD_HAND_LIMIT, CARD_REPLENISH_CONTACTS } from '../game/simulation/cards/cardStock';
import { EMPTY_RECORD, type GameRecord } from '../game/storage/recordStore';
import type { SessionEvent, SessionState } from '../game/simulation/types';
import { PhaseBackground } from './PhaseBackground';
import { cardPresentation } from './cardPresentation';
import { sideMenuMarkup } from './sideMenu';
import { EconomyUI } from './gacha/EconomyUI';
import { RankingUI } from './ranking/RankingUI';
import { RankingController } from '../app/RankingController';
import { ProfileUI } from './profile/ProfileUI';

const number = new Intl.NumberFormat('ja-JP');
const evolutionSections = [
  { id: 'first', label: '第一共鳴', range: '01 — 05', start: 1, end: 5 },
  { id: 'second', label: '第二共鳴', range: '06 — 10', start: 6, end: 10 },
  { id: 'third', label: '第三共鳴', range: '11 — 15', start: 11, end: 15 },
  { id: 'fourth', label: '第四共鳴', range: '16 — 20', start: 16, end: 20 },
  { id: 'final', label: '最終共鳴', range: '21 — 30', start: 21, end: 30 },
] as const;
const modalIds = ['settings-screen', 'evolution-screen', 'result-screen', 'pause-screen', 'start-screen'] as const;
type ModalId = (typeof modalIds)[number];

export class AppShell {
  private economyOpen = false;
  private readonly background = new PhaseBackground();
  private readonly root: HTMLElement;
  private record: GameRecord = EMPTY_RECORD;
  private lastState: SessionState | null = null;
  private loadingComplete = false;
  private muted = false;
  private autoEnabled = false;
  private resultTimer: number | null = null;
  private announcementTimer: number | null = null;
  private activeModalId: ModalId | null = null;
  private focusBeforeModal: HTMLElement | null = null;
  private pendingCardFocusSlot: number | null = null;
  private lastCardMarkup = '';
  private revealedStage = OBSERVER_STAGE;
  private resumeAfterEvolutionDiagram = false;
  private pauseScreenWasVisibleBeforeEvolution = false;
  private resumeAfterSettings = false;

  public constructor(root: HTMLElement) {
    this.root = root;
    this.render();
    this.bind();
    this.syncModalIsolation();
    const player = new RankingController();
    const onModalChange = (open: boolean): void => { this.economyOpen = open; this.syncModalIsolation(); };
    const ranking = new RankingUI(root, onModalChange, player);
    new ProfileUI(root, onModalChange, player, () => ranking.open());
    new EconomyUI(root, open => { this.economyOpen = open; this.syncModalIsolation(); });
  }

  private render(): void {
    this.root.innerHTML = `
      ${sideMenuMarkup()}
      <main class="app-shell">
        <header class="brand-bar">
          <div>
            <p class="eyebrow">RESONANCE OF WILL</p>
            <h1>意志の共鳴</h1>
          </div>
          <div class="brand-actions">
            <button id="evolution-button" class="icon-button icon-button--guide" type="button" aria-haspopup="dialog" aria-controls="evolution-screen">進化図</button>
            <p class="brand-mark" aria-hidden="true">三十の進化、その果てへ。</p>
          </div>
        </header>

        <section class="game-layout" aria-label="ゲーム画面">
          <aside class="hud-panel hud-panel--left" aria-label="現在の記録">
            <div class="metric metric--score">
              <p id="current-mode" class="current-mode">通常</p>
              <span>今回のスコア</span>
              <strong id="score">0</strong>
              <div class="ranking-clock"><span id="ranking-clock-label">到達タイム</span><b id="ranking-clock">00:00</b></div>
            </div>
            <div class="metric-row">
              <div class="metric"><span>共鳴</span><strong id="phase">第一</strong></div>
              <div class="metric"><span>連鎖</span><strong id="chain">—</strong></div>
            </div>
            <div class="record-card">
              <p>最高記録</p>
              <strong id="high-score">0</strong>
              <dl>
                <div><dt>最高進化</dt><dd id="high-stage">1</dd></div>
                <div><dt>最大連鎖</dt><dd id="high-chain">0</dd></div>
              </dl>
            </div>
            <div class="controls" aria-label="ゲーム操作">
              <button id="pause-button" class="icon-button" type="button" aria-pressed="false">一時停止</button>
              <button id="restart-button" class="icon-button icon-button--quiet" type="button">最初から</button>
              <button id="mute-button" class="icon-button icon-button--quiet" type="button" aria-pressed="false">音あり</button>
              <label class="volume-control" for="volume">
                <span>音量</span>
                <input id="volume" type="range" min="0" max="100" value="72" />
              </label>
            </div>
            <p id="danger-status" class="danger-status" data-level="safe" role="status" aria-live="assertive" aria-atomic="true">
              <span>敗北条件</span><strong id="danger-message">枠の外へ落下</strong>
            </p>
            <p class="input-help"><kbd>←</kbd><kbd>→</kbd> 移動　<kbd>Enter</kbd> 落下<br />クリック／指を離すと落下。確定まで時間制限なし<br />積むほどカメラが上へ移動。1体でも枠の外へ落ちると終了</p>
          </aside>

          <section class="playfield-frame">
            <div class="playfield-crown" aria-hidden="true"><span></span><i></i><span></span></div>
            <div id="game-canvas" aria-label="キャラクターを落とす盤面"></div>
            <svg width="0" height="0" aria-hidden="true" focusable="false" style="position:absolute">
              <defs><filter id="frame-remove-black" color-interpolation-filters="sRGB">
                <feColorMatrix type="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  20 20 20 0 -0.08" />
              </filter></defs>
            </svg>
            <div class="playfield-frame-art" aria-hidden="true"></div>
            <div id="announcement" class="announcement" aria-live="polite" aria-atomic="true"></div>
          </section>

            <section class="hand-panel" aria-label="意志のカード">
              <section id="card-boundary" class="card-boundary" aria-label="意志の手札"></section>
            </section>

          <aside class="hud-panel hud-panel--right" aria-label="次のキャラクター">
              <div id="evolution-peek" class="evolution-peek"><span>今の石を2つ重ねると</span><div><img id="merge-current-image" alt="" /><span>×2 →</span><img id="merge-result-image" alt="" /><strong id="merge-result-name">—</strong></div></div>
          <div class="card-sidebar">
            <section class="journey" aria-label="進化の到達点">
              <div><span>今回の最高進化</span><strong><b id="journey-stage">1</b><small id="journey-total"> / 30</small></strong></div>
              <progress id="journey-progress" max="30" value="1" aria-label="30段階の進化への到達度"></progress>
              <p id="journey-name">弱い石</p>
            </section>
          </div>
          <section class="hand-resources" aria-label="布石とカード入替">
              <div class="hand-title"><h2>意志のカード</h2><span>一手を変える力</span></div>
              <div class="stone-meter" aria-label="カードに使える布石"><span class="stone-symbol" aria-hidden="true">◆</span><span>布石<small>カード用ポイント</small></span><strong id="stones">0</strong><small>/ 99</small></div>
              <div id="card-renewal-status"></div>
          </section>
            <div class="next-card">
              <span>次の意志</span>
              <div class="next-image-wrap"><img id="next-image" alt="" /></div>
              <strong id="next-name">—</strong>
              <small id="next-stage">進化段階 —</small>
              <div id="second-next" class="second-next" hidden>
                <span>その次</span><img id="second-next-image" alt="" /><strong id="second-next-name">—</strong>
              </div>
            </div>
          </aside>
        </section>

        <div id="start-screen" class="screen-layer screen-layer--visible" role="dialog" aria-modal="false" aria-labelledby="start-title" aria-hidden="false" tabindex="-1">
          <div class="screen-card screen-card--start">
            <p class="screen-kicker">意志の共鳴</p>
            <h2 id="start-title">重ねて、共鳴させる。</h2>
            <p>意志を重ね、異形へと至る。<br />三十の進化を辿り、盤理の観測者へ。</p>
            <fieldset id="mode-selector" class="mode-selector" disabled aria-describedby="mode-description mode-ranking-note">
              <legend>ゲームモード</legend>
              <div class="mode-options">${GAME_MODES.map(mode => `<label class="mode-option"><input type="radio" name="game-mode" value="${mode.id}" ${mode.id === DEFAULT_GAME_MODE ? 'checked' : ''}/><span><strong>${mode.name}</strong><small>${mode.caption}</small></span></label>`).join('')}</div>
              <p id="mode-description">${getGameMode().description}</p>
              <p id="mode-route" class="mode-route">10 → 15 → 20 → 25 → 30</p>
              <p id="mode-ranking-note" class="mode-ranking-note">通常モードの手動プレイはランキング対象です。</p>
            </fieldset>
            <div class="merge-demo" aria-label="弱い石が2つ触れると増殖石に進化">
              <img src="${characterAssetPath('weak-stone')}" alt="弱い石" />
              <span aria-hidden="true">＋</span>
              <img src="${characterAssetPath('weak-stone')}" alt="弱い石" />
              <span aria-hidden="true">→</span>
              <img class="merge-demo__result" src="${characterAssetPath('proliferation-stone')}" alt="増殖石" />
            </div>
            <div class="start-rules"><p><b>01</b> 位置を決めて、タップで落とす</p><p><b>02</b> 同じ石が触れると、ひとつ進化</p><p><b>03</b> 枠の外へ落ちた時、共鳴は終わる</p></div>
            <div class="loading" aria-live="polite">
              <span id="loading-label">意志を呼び集めています…</span>
              <div class="loading-track"><i id="loading-bar"></i></div>
            </div>
            <div class="start-actions">
              <button id="start-button" class="primary-button" type="button" disabled>共鳴を始める</button>
              <button id="start-auto-button" class="icon-button start-auto-button" type="button" disabled>オート</button>
            </div>
            <p class="start-auto-help">配置とカードをおまかせ。途中で手動に切替できます。</p>
          </div>
        </div>

        <div id="pause-screen" class="screen-layer" role="dialog" aria-modal="false" aria-labelledby="pause-title" aria-hidden="true" tabindex="-1">
          <div class="screen-card screen-card--compact">
            <p class="screen-kicker">共鳴中断</p>
            <h2 id="pause-title">静止する意志</h2>
            <button id="resume-button" class="primary-button" type="button">ゲームへ戻る</button>
            <button id="pause-mode-button" class="icon-button mode-menu-button" type="button">プレイを終えてモード選択へ</button>
            <button id="pause-ranking-button" class="ranking-link-button" type="button" aria-haspopup="dialog" aria-controls="ranking-dialog">共鳴の殿堂を見る</button>
            <details class="auto-settings"><summary>自動プレイ</summary><button id="auto-button" class="icon-button auto-button" type="button" aria-pressed="false" aria-describedby="auto-help">オート</button><p id="auto-help" class="auto-help" role="status">配置とカードを自動で判断</p></details>
          </div>
        </div>

        <div id="result-screen" class="screen-layer" role="dialog" aria-modal="false" aria-labelledby="result-title" aria-hidden="true" tabindex="-1">
          <div class="screen-card screen-card--result">
            <p class="screen-kicker">共鳴の終焉</p>
            <h2 id="result-title">共鳴の記録</h2>
            <p id="result-mode"></p>
            <strong id="result-score" class="result-score">0</strong>
            <div id="result-ranking-summary" class="ranking-result-summary" role="status"></div>
            <dl class="result-grid">
              <div><dt>最高進化</dt><dd id="result-stage">1</dd></div>
              <div><dt>最大連鎖</dt><dd id="result-chain">0</dd></div>
              <div><dt>到達フェーズ</dt><dd id="result-phase">第一共鳴</dd></div>
            </dl>
            <button id="result-restart-button" class="primary-button" type="button">もう一度共鳴する</button>
            <button id="result-mode-button" class="icon-button mode-menu-button" type="button">モード選択へ</button>
            <button id="result-ranking-button" class="ranking-link-button" type="button" aria-haspopup="dialog" aria-controls="ranking-dialog">ランキングを見る</button>
            <button id="result-auto-button" class="icon-button start-auto-button" type="button">オートでもう一度</button>
          </div>
        </div>

        <div id="evolution-screen" class="screen-layer" role="dialog" aria-modal="true" aria-labelledby="evolution-title" aria-hidden="true" tabindex="-1">
          <div class="screen-card screen-card--evolution">
            <div class="evolution-header">
              <div>
                <p class="screen-kicker">EVOLUTION CHART</p>
                <h2 id="evolution-title">進化の系譜</h2>
              </div>
              <button id="evolution-close" class="icon-button icon-button--quiet evolution-close" type="button">閉じる</button>
            </div>
            <div class="evolution-intro">
              <p id="evolution-rules">同じ段階の意志を2つ重ねると、次の姿へ。共鳴が進むと石が小さくなり、盤面が整理されます。</p>
              <p id="evolution-progress" class="evolution-progress">今回の最高進化：段階 1 / 30</p>
            </div>
            <div class="evolution-map" aria-label="段階1から段階30までの進化順">
              ${this.evolutionMarkup()}
            </div>
            <div class="evolution-legend" aria-label="進化図の見方">
              <span><i class="evolution-legend__swatch evolution-legend__swatch--current"></i>今回の到達点</span>
              <span><i class="evolution-legend__arrow">→</i>同じ段階を2つ重ねる</span>
              <span><i class="evolution-legend__swatch evolution-legend__swatch--final"></i>終着点</span>
            </div>
          </div>
        </div>
        <div id="settings-screen" class="screen-layer" role="dialog" aria-modal="true" aria-labelledby="settings-title" aria-hidden="true" tabindex="-1">
          <div class="screen-card screen-card--compact screen-card--settings">
            <div class="settings-header">
              <h2 id="settings-title">設定</h2>
              <button id="settings-close" class="icon-button icon-button--quiet" type="button">閉じる</button>
            </div>
            <div class="settings-sound">
              <span>ゲームの音</span>
              <button id="settings-mute-button" class="icon-button" type="button" aria-pressed="false">音あり</button>
            </div>
            <label class="settings-volume" for="settings-volume">
              <span>音量</span><output id="settings-volume-value" for="settings-volume">72%</output>
              <input id="settings-volume" type="range" min="0" max="100" value="72" />
            </label>
          </div>
        </div>
      </main>
    `;
  }

  private bind(): void {
    this.byId('mode-selector').addEventListener('change', event => {
      const input = event.target as HTMLInputElement;
      const mode = GAME_MODES.find(mode => mode.id === input.value);
      if (mode && input.checked) runtimeBus.sendUi({ type: 'select-mode', mode: mode.id });
    });
    for (const id of ['pause-mode-button', 'result-mode-button']) {
      this.byId(id).addEventListener('click', () => runtimeBus.sendUi({ type: 'mode-menu' }));
    }
    for (const id of ['auto-button', 'start-auto-button', 'result-auto-button']) {
      this.byId(id).addEventListener('click', () => runtimeBus.sendUi({ type: 'auto-toggle' }));
    }
    runtimeBus.onAuto(enabled => {
      this.autoEnabled = enabled;
      this.updateAuto();
      if (this.lastState) this.renderCards(this.lastState);
    });
    this.byId('start-button').addEventListener('click', () => runtimeBus.sendUi({ type: 'start' }));
    this.byId('pause-button').addEventListener('click', () => runtimeBus.sendUi({ type: 'pause-toggle' }));
    this.byId('resume-button').addEventListener('click', () => runtimeBus.sendUi({ type: 'pause-toggle' }));
    this.byId('restart-button').addEventListener('click', () => runtimeBus.sendUi({ type: 'restart' }));
    this.byId('result-restart-button').addEventListener('click', () => runtimeBus.sendUi({ type: 'restart' }));
    this.byId('evolution-button').addEventListener('click', () => this.openEvolutionDiagram());
    this.byId('evolution-close').addEventListener('click', () => this.closeEvolutionDiagram());
    this.byId('settings-button').addEventListener('click', () => this.openSettings());
    this.byId('settings-close').addEventListener('click', () => this.closeSettings());
    for (const id of ['mute-button', 'settings-mute-button']) {
      this.byId(id).addEventListener('click', () => {
        this.muted = !this.muted;
        for (const buttonId of ['mute-button', 'settings-mute-button']) {
          const button = this.byId<HTMLButtonElement>(buttonId);
          button.textContent = this.muted ? '消音中' : '音あり';
          button.setAttribute('aria-pressed', String(this.muted));
        }
        runtimeBus.sendUi({ type: 'mute-toggle' });
      });
    }
    for (const id of ['volume', 'settings-volume']) {
      this.byId<HTMLInputElement>(id).addEventListener('input', (event) => {
        const value = (event.target as HTMLInputElement).value;
        for (const inputId of ['volume', 'settings-volume']) this.byId<HTMLInputElement>(inputId).value = value;
        this.byId('settings-volume-value').textContent = `${value}%`;
        runtimeBus.sendUi({ type: 'volume', value: Number(value) / 100 });
      });
    }
    this.byId('card-boundary').addEventListener('click', (event) => {
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>('button[data-card-instance]');
      const state = this.lastState;
      if (!button || !state || button.disabled) return;
      const instanceId = button.dataset.cardInstance;
      const cardId = button.dataset.cardId;
      if (!instanceId || !cardId) return;
      this.pendingCardFocusSlot = Number(button.dataset.cardSlot);
      runtimeBus.sendUi({
        type: 'card-use',
        generation: state.generation,
        instanceId,
        targetEntityId: this.cardTarget(cardId),
      });
    });
    runtimeBus.onLoading((progress, error) => this.updateLoading(progress, error));
    runtimeBus.onState((state, events) => this.updateState(state, events));
    runtimeBus.onRecord((record) => {
      this.record = record;
      this.updateRecord();
    });
    runtimeBus.onDanger((level) => this.updateDanger(level));
    this.root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.activeModalId === 'settings-screen') {
        event.preventDefault();
        event.stopPropagation();
        this.closeSettings();
        return;
      }
      if (event.key === 'Escape' && this.activeModalId === 'evolution-screen') {
        event.preventDefault();
        this.closeEvolutionDiagram();
        return;
      }
      this.trapModalFocus(event);
    });
  }

  private updateLoading(progress: number, error?: string): void {
    this.byId<HTMLElement>('loading-bar').style.width = `${Math.round(progress * 100)}%`;
    const label = this.byId('loading-label');
    const button = this.byId<HTMLButtonElement>('start-button');
    this.loadingComplete = !error && progress >= 1;
    // Assets may finish before GameScene has subscribed to start actions.
    const ready = this.loadingComplete && this.lastState !== null;
    button.disabled = !ready;
    this.byId<HTMLButtonElement>('start-auto-button').disabled = !ready;
    this.byId<HTMLFieldSetElement>('mode-selector').disabled = !ready;
    if (error) {
      label.textContent = error;
      label.classList.add('loading-error');
      button.disabled = true;
    } else if (progress >= 1) {
      label.textContent = ready ? '準備が整いました' : '盤面を準備しています…';
      if (ready && this.activeModalId === 'start-screen') this.root.querySelector<HTMLInputElement>('input[name="game-mode"]:checked')?.focus({ preventScroll: true });
    } else {
      label.textContent = `意志を呼び集めています… ${Math.round(progress * 100)}%`;
    }
  }

  private updateState(state: SessionState, events: readonly SessionEvent[]): void {
    const firstState = this.lastState === null;
    const modeChanged = this.lastState?.mode !== state.mode;
    this.lastState = state;
    if (modeChanged) {
      const mode = getGameMode(state.mode);
      this.root.querySelectorAll<HTMLInputElement>('input[name="game-mode"]').forEach(input => { input.checked = input.value === state.mode; });
      this.byId('mode-description').textContent = mode.description;
      this.byId('mode-route').textContent = `到達目標：${mode.phases.map(phase => phase.goalStage).join(' → ')}段階`;
      this.byId('mode-ranking-note').textContent = state.mode === 'classic' ? '通常モードの手動プレイはランキング対象です。' : '最高記録はモード別に保存。追加モードはランキング対象外です。';
      this.byId('evolution-rules').textContent = mode.carryMilestone
        ? '同じ段階の意志を2つ重ねると、次の姿へ。共鳴が進むと石が小さくなり、盤面が整理されます。'
        : '同じ段階を2つ重ねると次の姿へ。各区間の最後のキャラに到達すると盤面が空になり、次の区間のキャラだけが登場します。';
      this.root.querySelector('.evolution-map')!.innerHTML = this.evolutionMarkup();
    }
    if (firstState && this.loadingComplete) this.updateLoading(1);
    this.background.update(presentationPhase(state.mode, state.phase));
    this.byId('current-mode').textContent = `${getGameMode(state.mode).name} (${state.phase}/${getGameMode(state.mode).phases.length})`;
    this.byId('score').textContent = number.format(state.score);
    this.byId('phase').textContent = phaseName(state.mode, state.phase).replace('共鳴', '');
    this.byId('chain').textContent = state.chain > 0 ? `${state.chain}` : '—';
    this.updateEvolutionProgress(state.highestStage);
    const next = getCharacter(state.dropView.slotActive ? state.dropView.currentCharacterId : state.dropView.nextCharacterId);
    const nextImage = this.byId<HTMLImageElement>('next-image');
    nextImage.src = characterAssetPath(next.id);
    nextImage.alt = `次は${next.name}`;
    this.byId('next-name').textContent = next.name;
    this.byId('next-stage').textContent = `進化段階 ${next.stage}`;
    const secondNext = this.byId<HTMLElement>('second-next');
    const second = getCharacter(state.dropView.secondNextCharacterId);
    const showSecond = state.cardEffects.observeUntilMs !== null;
    secondNext.hidden = !showSecond;
    this.byId<HTMLImageElement>('second-next-image').src = characterAssetPath(second.id);
    this.byId<HTMLImageElement>('second-next-image').alt = showSecond ? `その次は${second.name}` : '';
    this.byId('second-next-name').textContent = second.name;
    this.byId('stones').textContent = number.format(state.card.stones);
    const currentId = state.entities.find(entity => entity.entityId === state.activeDropEntityId)?.characterId ?? state.dropView.currentCharacterId;
    const current = getCharacter(currentId);
    const candidate = CHARACTERS[current.stage];
    const evolved = candidate && candidate.stage <= this.revealedStage ? candidate : undefined;
    this.byId('evolution-peek').hidden = !evolved;
    if (evolved) {
      this.byId<HTMLImageElement>('merge-current-image').src = characterAssetPath(current.id);
      this.byId<HTMLImageElement>('merge-current-image').alt = current.name;
      this.byId<HTMLImageElement>('merge-result-image').src = characterAssetPath(evolved.id);
      this.byId<HTMLImageElement>('merge-result-image').alt = evolved.name;
      this.byId('merge-result-name').textContent = evolved.name;
    }
    this.renderCards(state);

    this.setLayer('start-screen', state.status === 'ready');
    this.setLayer('pause-screen', state.status === 'paused');
    const pauseButton = this.byId<HTMLButtonElement>('pause-button');
    pauseButton.textContent = state.status === 'paused' ? '再開する' : '一時停止';
    pauseButton.setAttribute('aria-pressed', String(state.status === 'paused'));
    this.updateAuto();

    if (state.status !== 'game-over') {
      if (this.resultTimer !== null) window.clearTimeout(this.resultTimer);
      this.resultTimer = null;
      this.setLayer('result-screen', false);
    } else if (this.resultTimer === null) {
      const generation = state.generation;
      this.resultTimer = window.setTimeout(() => {
        this.resultTimer = null;
        if (this.lastState?.generation === generation && this.lastState.status === 'game-over') {
          this.showResult(this.lastState);
        }
      }, 620);
    }

    this.syncModalIsolation();

    for (const event of events) this.presentEvent(event);
  }

  private presentEvent(event: SessionEvent): void {
    if (event.type === 'merge') {
      this.announce(`${event.chain}連鎖　+${number.format(event.awardedScore)}`, 1_200, true);
    } else if (event.type === 'phase-changed') {
      this.announce(`${phaseName(this.lastState?.mode ?? DEFAULT_GAME_MODE, event.phase)}へ昇華　+${number.format(event.bonus)}`);
    } else if (event.type === 'achievement') {
      this.announce(event.text, 3_000);
    } else if (event.type === 'card-use') {
      this.announce(event.success ? '意志を使用しました' : event.reason ?? '今は使用できません');
    }
  }

  private updateAuto(): void {
    const button = this.byId<HTMLButtonElement>('auto-button');
    button.textContent = this.autoEnabled ? 'オート中 · 解除' : 'オート';
    button.setAttribute('aria-pressed', String(this.autoEnabled));
    this.byId('auto-help').textContent = this.autoEnabled
      ? this.lastState?.status === 'paused' ? 'オートは一時停止中' : '自動プレイ中 · 解除で手動に戻る'
      : '配置とカードを自動で判断';
  }

  private showResult(state: SessionState): void {
    this.byId('result-mode').textContent = getGameMode(state.mode).name;
    this.byId('result-score').textContent = number.format(state.score);
    this.byId('result-stage').textContent = String(state.highestStage);
    this.byId('result-chain').textContent = String(state.maxChain);
    this.byId('result-phase').textContent = phaseName(state.mode, state.phase);
    this.setLayer('result-screen', true);
    this.syncModalIsolation();
  }

  private updateRecord(): void {
    this.byId('high-score').textContent = number.format(this.record.highScore);
    this.byId('high-stage').textContent = String(this.record.highestStage);
    this.byId('high-chain').textContent = String(this.record.maxChain);
    this.updateEvolutionProgress(this.lastState?.highestStage ?? 1);
  }

  private openEvolutionDiagram(): void {
    if (this.activeModalId !== null) return;
    this.pauseScreenWasVisibleBeforeEvolution = this.byId('pause-screen').classList.contains('screen-layer--visible');
    this.resumeAfterEvolutionDiagram = this.lastState?.status === 'playing';
    this.setLayer('evolution-screen', true);
    this.syncModalIsolation();
    if (this.resumeAfterEvolutionDiagram) runtimeBus.sendUi({ type: 'pause-toggle' });
    this.setLayer('pause-screen', false);
    this.syncModalIsolation();
  }

  private openSettings(): void {
    if (this.activeModalId === 'settings-screen' || this.activeModalId === 'evolution-screen') return;
    this.resumeAfterSettings = this.lastState?.status === 'playing';
    this.setLayer('settings-screen', true);
    this.byId('settings-button').setAttribute('aria-expanded', 'true');
    this.syncModalIsolation();
    if (this.resumeAfterSettings) runtimeBus.sendUi({ type: 'pause-toggle' });
  }

  private closeSettings(): void {
    if (this.activeModalId !== 'settings-screen') return;
    const resume = this.resumeAfterSettings;
    this.resumeAfterSettings = false;
    this.setLayer('settings-screen', false);
    this.byId('settings-button').setAttribute('aria-expanded', 'false');
    if (resume && this.lastState?.status === 'paused') runtimeBus.sendUi({ type: 'pause-toggle' });
    this.syncModalIsolation();
    window.requestAnimationFrame(() => this.byId('settings-button').focus());
  }

  private closeEvolutionDiagram(): void {
    if (!this.byId('evolution-screen').classList.contains('screen-layer--visible')) return;
    const resume = this.resumeAfterEvolutionDiagram;
    const restorePause = this.pauseScreenWasVisibleBeforeEvolution;
    this.resumeAfterEvolutionDiagram = false;
    this.pauseScreenWasVisibleBeforeEvolution = false;
    this.setLayer('evolution-screen', false);
    if (restorePause) this.setLayer('pause-screen', true);
    this.syncModalIsolation();
    if (resume && this.lastState?.status === 'paused') runtimeBus.sendUi({ type: 'pause-toggle' });
  }

  private evolutionMarkup(): string {
    const mode = getGameMode(this.lastState?.mode ?? DEFAULT_GAME_MODE);
    const modeSections = mode.id === 'classic' ? evolutionSections : mode.phases.map((phase, index) => ({
      id: `chapter-${index + 1}`, label: phaseName(mode.id, index + 1), start: phase.firstStage, end: phase.goalStage,
      range: `${String(phase.firstStage).padStart(2, '0')} — ${phase.goalStage}`,
    }));
    const sections = [...modeSections, ...(this.revealedStage > OBSERVER_STAGE ? [{
      id: 'hidden', label: '観測の先', range: `31 — ${this.revealedStage}`, start: 31, end: this.revealedStage,
    }] : [])];
    return sections.map((section) => {
      const characters = CHARACTERS.slice(section.start - 1, section.end);
      const nodes = characters.map((character) => {
        const finalBadge = character.stage === 30
          ? '<span class="evolution-node__badge">クリア到達</span>'
          : character.stage === 29
            ? '<span class="evolution-node__badge">特殊進化</span>'
            : character.stage > OBSERVER_STAGE ? '<span class="evolution-node__badge">隠し進化</span>' : '';
        const modifier = character.stage === 30 ? ' evolution-node--final' : '';
        return `<li class="evolution-node${modifier}" data-evolution-stage="${character.stage}" title="進化段階 ${character.stage}：${character.name}">
          <span class="evolution-node__stage">STAGE ${String(character.stage).padStart(2, '0')}</span>
          <div class="evolution-node__image-wrap"><img src="${characterAssetPath(character.id)}" alt="" decoding="async" /></div>
          <span class="evolution-node__name">${character.name}</span>
          ${finalBadge}
        </li>`;
      }).join('');
      return `<section class="evolution-phase" aria-labelledby="evolution-phase-${section.id}">
        <div class="evolution-phase__header"><h3 id="evolution-phase-${section.id}">${section.label}</h3><span>段階 ${section.range}</span></div>
        <div class="evolution-phase__track"><ol class="evolution-phase__nodes">${nodes}</ol></div>
      </section>`;
    }).join('');
  }

  private updateEvolutionProgress(highestStage: number): void {
    const stage = Math.max(1, Math.min(CHARACTERS.length, highestStage));
    const revealed = Math.max(OBSERVER_STAGE, stage, Math.min(CHARACTERS.length, this.record.highestStage));
    if (revealed !== this.revealedStage) {
      this.revealedStage = revealed;
      const map = this.root.querySelector<HTMLElement>('.evolution-map')!;
      map.innerHTML = this.evolutionMarkup();
      map.setAttribute('aria-label', `段階1から段階${revealed}までの進化順`);
    }
    this.byId('journey-stage').textContent = String(stage);
    this.byId('journey-total').textContent = ` / ${revealed}`;
    const progress = this.byId<HTMLProgressElement>('journey-progress');
    progress.max = revealed;
    progress.value = stage;
    progress.setAttribute('aria-label', `${revealed}段階の進化への到達度`);
    this.byId('journey-name').textContent = CHARACTERS.find((character) => character.stage === stage)?.name ?? '';
    this.byId('evolution-progress').textContent = `今回の最高進化：段階 ${stage} / ${revealed}`;
    this.root.querySelectorAll<HTMLElement>('[data-evolution-stage]').forEach((node) => {
      const nodeStage = Number(node.dataset.evolutionStage);
      node.classList.toggle('evolution-node--reached', nodeStage <= stage);
      node.classList.toggle('evolution-node--current', nodeStage === stage);
    });
  }

  private updateDanger(level: DangerLevel): void {
    const status = this.byId('danger-status');
    const message = this.byId('danger-message');
    status.dataset.level = level;
    message.textContent = '枠の外へ落下';
  }

  private announce(message: string, duration = 1_200, isChain = false): void {
    const announcement = this.byId('announcement');
    if (this.announcementTimer !== null) window.clearTimeout(this.announcementTimer);
    announcement.textContent = message;
    announcement.classList.toggle('announcement--chain', isChain);
    announcement.classList.remove('announcement--visible');
    window.requestAnimationFrame(() => announcement.classList.add('announcement--visible'));
    this.announcementTimer = window.setTimeout(() => {
      this.announcementTimer = null;
      announcement.classList.remove('announcement--visible');
    }, duration);
  }

  private setLayer(id: string, visible: boolean): void {
    const layer = this.byId(id);
    layer.classList.toggle('screen-layer--visible', visible);
    layer.setAttribute('aria-hidden', String(!visible));
  }

  private syncModalIsolation(): void {
    if (this.economyOpen) {
      this.root.querySelectorAll<HTMLElement>('.brand-bar, .game-layout, .side-menu, .screen-layer').forEach(element => { element.inert = true; });
      return;
    }
    const next = modalIds.find((id) => this.byId(id).classList.contains('screen-layer--visible')) ?? null;
    const changed = next !== this.activeModalId;
    if (changed && this.activeModalId === null && next !== null) {
      this.focusBeforeModal = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    }
    this.activeModalId = next;

    const isolated = next !== null;
    this.root.querySelectorAll<HTMLElement>('.brand-bar, .game-layout').forEach((element) => {
      element.inert = isolated;
    });
    // Start, pause and result screens leave the navigation available.
    this.root.querySelector<HTMLElement>('.side-menu')!.inert = next === 'settings-screen' || next === 'evolution-screen';
    for (const id of modalIds) this.byId(id).inert = id !== next;

    if (!changed) return;
    window.requestAnimationFrame(() => {
      if (this.activeModalId !== next) return;
      if (next) {
        const targetId = next === 'settings-screen'
          ? 'settings-close'
          : next === 'start-screen'
          ? 'start-button'
          : next === 'pause-screen'
            ? 'resume-button'
            : next === 'evolution-screen'
              ? 'evolution-close'
            : 'result-restart-button';
        const target = this.byId<HTMLButtonElement>(targetId);
        const modeChoice = next === 'start-screen' ? this.root.querySelector<HTMLInputElement>('input[name="game-mode"]:checked') : null;
        if (modeChoice && !this.byId<HTMLFieldSetElement>('mode-selector').disabled) modeChoice.focus({ preventScroll: true });
        else if (!target.disabled) target.focus();
        else this.byId(next).focus();
        return;
      }
      const target = this.focusBeforeModal;
      this.focusBeforeModal = null;
      if (target?.isConnected && !target.inert) target.focus();
      else this.byId<HTMLButtonElement>('pause-button').focus();
    });
  }

  private trapModalFocus(event: KeyboardEvent): void {
    if (this.economyOpen) return;
    if (event.key !== 'Tab' || this.activeModalId === null) return;
    if (this.activeModalId !== 'settings-screen' && this.activeModalId !== 'evolution-screen') return;
    const layer = this.byId(this.activeModalId);
    const focusable = [...layer.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')]
      .filter((element) => element.getClientRects().length > 0);
    if (focusable.length === 0) {
      event.preventDefault();
      layer.focus();
      return;
    }
    const current = document.activeElement;
    const currentIndex = focusable.indexOf(current as HTMLElement);
    const target = event.shiftKey
      ? currentIndex <= 0 ? focusable[focusable.length - 1] : null
      : currentIndex === -1 || currentIndex === focusable.length - 1 ? focusable[0] : null;
    if (!target) return;
    event.preventDefault();
    target.focus();
  }

  private byId<T extends HTMLElement = HTMLElement>(id: string): T {
    const element = this.root.querySelector<T>(`#${id}`);
    if (!element) throw new Error(`Missing UI element #${id}`);
    return element;
  }

  private renderCards(state: SessionState): void {
    const availability = new Map(state.cardAvailability.map((entry) => [entry.instanceId, entry]));
      const effectLabels = this.effectLabels(state);
      const slots = Array.from({ length: CARD_HAND_LIMIT }, (_, index) => {
        const held = state.card.hand.find((entry) => entry.slot === index);
        if (!held) return `<div class="card-slot card-slot--empty" data-card-slot="${index}" tabindex="-1" aria-label="手札の空き枠">空き枠</div>`;
        const card = getCard(held.cardId);
        const enabled = !this.autoEnabled && (availability.get(held.instanceId)?.enabled ?? false);
        const reason = this.autoEnabled ? 'オートが使用を判断します' : availability.get(held.instanceId)?.reason;
        const info = cardPresentation(state, held.cardId, enabled, reason, this.autoEnabled);
        const effect = effectLabels.get(held.cardId);
        return `<button class="will-card" type="button" data-card-instance="${held.instanceId}" data-card-id="${held.cardId}" data-card-slot="${held.slot}" ${enabled ? '' : 'disabled'} title="${reason ?? ''}">
          <span class="will-card__status">${info.badge}</span>
          <span class="will-card__seal" aria-hidden="true">${card.name.slice(0, 1)}</span>
          <span class="will-card__name">${card.name}</span><span class="will-card__cost">布石 <b>${card.cost}</b></span>
          <span class="will-card__description">${card.description}</span>${effect ? `<small>${effect}</small>` : ''}<small class="will-card__reason">${info.detail}</small>
        </button>`;
      });
      const remaining = CARD_REPLENISH_CONTACTS - state.card.normalContactsTowardReplacement;
      const activeEffects = [...effectLabels.entries()]
        .map(([cardId, label]) => `${getCard(cardId as CardId).name} ${label}`);
      const effectStatus = activeEffects.length === 0
        ? ''
        : `<p class="card-effect-status" role="status">有効中：${activeEffects.join(' ／ ')}</p>`;
      const boundary = this.byId('card-boundary');
      const renewalMarkup = `<div class="card-renewal"><span class="card-renewal__steps" aria-hidden="true">${Array.from({ length: CARD_REPLENISH_CONTACTS }, (_, i) => `<i class="${i < state.card.normalContactsTowardReplacement ? 'is-filled' : ''}"></i>`).join('')}</span>あと${remaining}回置くとカード入替</div>${effectStatus}`;
      const renewal = this.byId('card-renewal-status');
      if (renewal.innerHTML !== renewalMarkup) renewal.innerHTML = renewalMarkup;
      const markup = `<div class="card-hand">${slots.join('')}</div>`;
      if (markup !== this.lastCardMarkup) {
        const focused = document.activeElement instanceof HTMLElement && boundary.contains(document.activeElement) ? document.activeElement : null;
        if (this.pendingCardFocusSlot === null && focused?.dataset.cardSlot) this.pendingCardFocusSlot = Number(focused.dataset.cardSlot);
        this.lastCardMarkup = markup;
        boundary.innerHTML = markup;
      }
      if (this.pendingCardFocusSlot !== null) {
        const slot = this.pendingCardFocusSlot;
        this.pendingCardFocusSlot = null;
        window.requestAnimationFrame(() => boundary.querySelector<HTMLElement>(`[data-card-slot="${slot}"]`)?.focus());
      }
  }

  private cardTarget(cardId: string): string | null {
    const state = this.lastState;
    if (!state) return null;
    if (cardId === 'destroy' || cardId === 'buoyancy') return state.dropView.lastPlacedEntityId;
    if (cardId === 'swap' || cardId === 'reincarnate' || cardId === 'gravity' || cardId === 'attraction' || cardId === 'wind') {
      return state.dropView.activeEntityId;
    }
    return null;
  }

  private effectLabels(state: SessionState): Map<string, string> {
    const labels = new Map<string, string>();
    const remaining = (untilMs: number) => Math.max(0, Math.ceil((untilMs - state.gameplayTimeMs) / 1_000));
    if (state.cardEffects.observeUntilMs !== null) labels.set('observe', `残り ${remaining(state.cardEffects.observeUntilMs)}秒`);
    if (state.cardEffects.buoyancy) labels.set('buoyancy', `残り ${remaining(state.cardEffects.buoyancy.untilMs)}秒`);
    if (state.cardEffects.attraction) labels.set('attraction', `残り ${remaining(state.cardEffects.attraction.untilMs)}秒`);
    if (state.cardEffects.gravityEntityIds.length > 0) labels.set('gravity', '重さ×2');
    if (state.cardEffects.wind) labels.set('wind', state.cardEffects.wind.direction === 'left' ? '← 予約中' : '→ 予約中');
    return labels;
  }
}
